import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { LogIn, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';


const NewPortalLogin = () => {
  const { team_id } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requiresVerification, setRequiresVerification] = useState(false);

  useEffect(() => {
    // Check for admin auth data in hash and redirect to dashboard
    const hash = window.location.hash;
    if (hash.startsWith('#auth=')) {
      // Redirect to dashboard with the hash
      navigate(`/portal/${team_id}/dashboard${hash}`);
      return;
    }
    
    // Check if there's a success message from verification
    const params = new URLSearchParams(window.location.search);
    const verified = params.get('verified');
    if (verified === 'true') {
      setError('');
      // Show success message briefly
      const timer = setTimeout(() => {
        // Clear the URL parameter
        window.history.replaceState({}, '', window.location.pathname);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [navigate, team_id]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setRequiresVerification(false);
    setLoading(true);

    try {
      const response = await api.post(`/portal/${team_id}/login`, {
        email: email.trim().toLowerCase(),
        password: password
      });

      if (response.data.success) {
        // Store JWT token
        localStorage.setItem('portal_token', response.data.token);
        localStorage.setItem('portal_team_id', team_id);
        localStorage.setItem('portal_contact', JSON.stringify(response.data.contact));
        localStorage.setItem('portal_client', JSON.stringify(response.data.client));

        // Check if user has multiple clients
        if (response.data.available_clients && response.data.available_clients.length > 0) {
          // Navigate to client selector
          navigate(`/portal/${team_id}/select-client`, {
            state: { clients: response.data.available_clients }
          });
        } else {
          // Navigate directly to dashboard
          navigate(`/portal/${team_id}/dashboard`);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorData = err.response?.data;
      
      if (errorData?.error?.includes('verify your email')) {
        setRequiresVerification(true);
        setError('Please verify your email before logging in. Check your inbox for the verification link.');
      } else if (errorData?.error?.includes('set your password')) {
        setError('Please set your password first. Check your email for instructions.');
      } else if (errorData?.error?.includes('locked')) {
        setError('Account temporarily locked due to too many failed attempts. Please try again later.');
      } else if (errorData?.error?.includes('not enabled')) {
        setError('Portal access is not available. Please contact your administrator.');
      } else {
        setError(errorData?.error || 'Invalid email or password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-white/10 rounded-2xl backdrop-blur-sm mb-4">
            <LogIn className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Client Portal</h1>
          <p className="text-purple-200">Sign in to access your projects</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Success Message */}
          {window.location.search.includes('verified=true') && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-green-800 font-medium">Email verified successfully!</p>
                <p className="text-sm text-green-700 mt-1">You can now log in with your password.</p>
              </div>
            </div>
          )}

          {/* Password Set Success */}
          {window.location.search.includes('password_set=true') && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-green-800 font-medium">Password set successfully!</p>
                <p className="text-sm text-green-700 mt-1">You can now log in.</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
                {requiresVerification && (
                  <button
                    onClick={() => navigate(`/portal/${team_id}/resend-verification?email=${encodeURIComponent(email)}`)}
                    className="text-sm text-red-700 underline mt-2 hover:text-red-800"
                  >
                    Resend verification email
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="your@email.com"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <Link
                to={`/portal/${team_id}/forgot-password`}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                Forgot password?
              </Link>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              First time here?{' '}
              <span className="text-purple-600 font-medium">
                Check your email for your access invitation
              </span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-purple-200">
            Powered by Athena Project Management
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewPortalLogin;
